import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import SearchableDropdown from '../components/SearchableDropdown';
import { apiGet } from '../utils/api';
import { downloadApiFile } from '../utils/downloadApiFile';
import { toast } from 'react-hot-toast';
import AnalyticsChart from '../components/AnalyticsChart';
import {
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  X
} from '../components/Icons';

const DEFAULT_FILTERS = {
  dateRange: '30',
  dateFrom: '',
  dateTo: '',
  groupBy: 'day',
  status: 'all',
  fbrStatus: 'all',
  clientId: 'all',
  productId: 'all',
  categoryId: 'all'
};

const humanise = (value) =>
  String(value || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    role: 'tenant',
    totals: {
      totalRevenue: 0,
      totalInvoices: 0,
      totalClients: 0,
      totalProducts: 0,
      submittedToFbr: 0,
      approvedByFbr: 0,
      rejectedByFbr: 0
    },
    tenant: {
      id: null,
      name: null,
      hasToken: false,
      ntnCnic: null,
      province: null
    }
  });
  const [analyticsData, setAnalyticsData] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    products: [],
    categories: [],
    statuses: [],
    fbrStatuses: []
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Every keystroke on a date box refires the analytics calls. Without this
  // guard a slow earlier response can land after a newer one and put stale
  // numbers back on the screen.
  const latestRequest = useRef(0);

  // The same query string feeds all four charts and the Excel export, which is
  // what keeps the download matching what the tenant is looking at.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.dateRange === 'custom') {
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      if (!filters.dateFrom && !filters.dateTo) params.set('date_range', '30');
    } else {
      params.set('date_range', filters.dateRange);
    }
    params.set('status_filter', filters.status);
    params.set('fbr_status_filter', filters.fbrStatus);
    if (filters.clientId !== 'all') params.set('client_id', filters.clientId);
    if (filters.productId !== 'all') params.set('product_id', filters.productId);
    if (filters.categoryId !== 'all') params.set('category_id', filters.categoryId);
    return params;
  }, [filters]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const dashboardResponse = await apiGet('/api/tenant/dashboard');
      if (dashboardResponse.ok) {
        setDashboardData(dashboardResponse.data);
      } else {
        console.error('Error fetching dashboard data:', dashboardResponse.data);
        toast.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await apiGet('/api/analytics/filter-options');
      if (response.ok) {
        setFilterOptions({
          clients: response.data.clients || [],
          products: response.data.products || [],
          categories: response.data.categories || [],
          statuses: response.data.statuses || [],
          fbrStatuses: response.data.fbr_statuses || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  const fetchAnalyticsData = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setAnalyticsLoading(true);

    const query = buildFilterParams().toString();
    try {
      const [salesTrends, inventoryAlerts, topPerformers, fbrCompliance] = await Promise.all([
        apiGet(`/api/analytics/sales-trends?${query}&group_by=${filters.groupBy}`),
        apiGet(`/api/analytics/inventory-alerts?alert_type=all${
          filters.categoryId !== 'all' ? `&category_id=${filters.categoryId}` : ''
        }`),
        apiGet(`/api/analytics/top-performers?${query}&top_n=5`),
        apiGet(`/api/analytics/fbr-compliance?${query}`)
      ]);

      if (requestId !== latestRequest.current) return;

      if (salesTrends.ok && inventoryAlerts.ok && topPerformers.ok && fbrCompliance.ok) {
        setAnalyticsData({
          salesTrends: salesTrends.data,
          inventoryAlerts: inventoryAlerts.data,
          topPerformers: topPerformers.data,
          fbrCompliance: fbrCompliance.data
        });
      } else {
        toast.error('Failed to load analytics data');
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      if (requestId === latestRequest.current) toast.error('Failed to load analytics data');
    } finally {
      if (requestId === latestRequest.current) setAnalyticsLoading(false);
    }
  }, [buildFilterParams, filters.groupBy, filters.categoryId]);

  useEffect(() => {
    fetchDashboardData();
    fetchFilterOptions();
  }, [fetchDashboardData, fetchFilterOptions]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Kept on one currency word. The Intl currency format writes "Rs 0", which
  // sat under a "PKR 1.3M" gridline on the same axis and read as two different
  // currencies.
  const formatLargeNumber = (amount) => {
    const value = Number(amount) || 0;
    if (value >= 1000000000) {
      return `PKR ${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `PKR ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `PKR ${(value / 1000).toFixed(1)}K`;
    }
    return `PKR ${Math.round(value).toLocaleString('en-PK')}`;
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  const activeFilterCount = [
    filters.status !== 'all',
    filters.fbrStatus !== 'all',
    filters.clientId !== 'all',
    filters.productId !== 'all',
    filters.categoryId !== 'all',
    filters.dateRange === 'custom' && (filters.dateFrom || filters.dateTo)
  ].filter(Boolean).length;

  const exportAnalytics = async () => {
    setExporting(true);
    try {
      const query = buildFilterParams().toString();
      await downloadApiFile(`/api/analytics/export?${query}`, 'axiom-sales.xlsx');
      toast.success('Excel file downloaded');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clientOptions = [{ id: 'all', name: 'All clients' }].concat(
    filterOptions.clients.map(c => ({ id: String(c.id), name: c.name || `Client ${c.id}` }))
  );
  const productOptions = [{ id: 'all', name: 'All products' }].concat(
    filterOptions.products.map(p => ({ id: String(p.id), name: p.name || `Product ${p.id}` }))
  );
  const categoryOptions = [{ id: 'all', name: 'All categories' }].concat(
    filterOptions.categories.map(c => ({ id: String(c.id), name: c.name || `Category ${c.id}` }))
  );

  // The status values differ per tenant: batch imports write draft, validated
  // and failed alongside the usual pending and submitted. Offering the values
  // the tenant's own invoices actually carry avoids a filter that matches
  // nothing. The fallbacks cover a brand new tenant with no invoices yet.
  const statusChoices = filterOptions.statuses.length
    ? filterOptions.statuses
    : ['pending', 'paid', 'cancelled'];
  const fbrStatusChoices = filterOptions.fbrStatuses.length
    ? filterOptions.fbrStatuses
    : ['not_submitted', 'submitted', 'validated', 'failed'];

  const periodSummary = analyticsData.salesTrends?.summary;
  const trendRows = analyticsData.salesTrends?.trends || [];

  const selectClass = 'w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <Navigation>
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-4 justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
                <p className="text-slate-600">Welcome back, {user?.name}</p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={fetchAnalyticsData}
                  disabled={analyticsLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${analyticsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={exportAnalytics}
                  disabled={exporting}
                  title="Download the filtered sales and invoices as an Excel file"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  <Download className={`h-4 w-4 inline mr-2 ${exporting ? 'animate-bounce' : ''}`} />
                  {exporting ? 'Preparing file...' : 'Download Excel'}
                </button>
              </div>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards (all time, not affected by the filters below) */}
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">All time totals</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
                <DollarSign className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">{formatLargeNumber(dashboardData.totals.totalRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-green-50 text-green-600">
                <FileText className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Total Invoices</p>
                <p className="text-2xl font-bold text-slate-900">{dashboardData.totals.totalInvoices}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-purple-50 text-purple-600">
                <Users className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Total Clients</p>
                <p className="text-2xl font-bold text-slate-900">{dashboardData.totals.totalClients}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-yellow-50 text-yellow-600">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-900">{dashboardData.totals.totalProducts}</p>
              </div>
            </div>
          </div>
        </div>

        {/* FBR Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                <Clock className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Submitted to FBR</p>
                <p className="text-2xl font-bold text-blue-600">{dashboardData.totals.submittedToFbr}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-green-50 text-green-600">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Approved by FBR</p>
                <p className="text-2xl font-bold text-green-600">{dashboardData.totals.approvedByFbr}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-red-50 text-red-600">
                <XCircle className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-500">Rejected by FBR</p>
                <p className="text-2xl font-bold text-red-600">{dashboardData.totals.rejectedByFbr}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tenant Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-lg font-medium text-slate-900">Tenant Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-500">Business Name</p>
                <p className="text-lg text-slate-900">{dashboardData.tenant.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">FBR Token Status</p>
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                  dashboardData.tenant.hasToken
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {dashboardData.tenant.hasToken ? 'Configured' : 'Not Configured'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">NTN/CNIC</p>
                <p className="text-lg text-slate-900">{dashboardData.tenant.ntnCnic || 'Not Set'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Province</p>
                <p className="text-lg text-slate-900">{dashboardData.tenant.province || 'Not Set'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Alerts */}
        {analyticsData.inventoryAlerts?.alerts?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-medium text-slate-900">Inventory Alerts</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analyticsData.inventoryAlerts.alerts.slice(0, 6).map((alert, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-900">{alert.product_name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        alert.alert_type === 'out_of_stock' ? 'bg-red-100 text-red-800' :
                        alert.alert_type === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {alert.alert_type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600">
                      <p>Current Stock: {alert.current_stock}</p>
                      <p>Min Level: {alert.min_stock_level}</p>
                      {alert.days_until_stockout < 999 && (
                        <p className="text-red-600">Stockout in {alert.days_until_stockout} days</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex items-center">
                <Filter className="h-5 w-5 text-slate-400 mr-2" />
                <h3 className="text-lg font-medium text-slate-900">Sales Analytics</h3>
                {activeFilterCount > 0 && (
                  <span className="ml-3 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                    {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {activeFilterCount > 0 && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center text-sm text-slate-600 hover:text-slate-900"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear filters
                  </button>
                )}
                <span className="text-sm text-slate-500">
                  {periodSummary?.date_range || ''}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className={selectClass}
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="180">Last 6 months</option>
                  <option value="365">Last year</option>
                  <option value="custom">Custom dates</option>
                </select>
              </div>

              {filters.dateRange === 'custom' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">From</label>
                    <input
                      type="date"
                      value={filters.dateFrom}
                      max={filters.dateTo || undefined}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                      className={selectClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">To</label>
                    <input
                      type="date"
                      value={filters.dateTo}
                      min={filters.dateFrom || undefined}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                      className={selectClass}
                    />
                  </div>
                </>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Group By</label>
                <select
                  value={filters.groupBy}
                  onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                  className={selectClass}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client</label>
                <SearchableDropdown
                  options={clientOptions}
                  value={filters.clientId}
                  onChange={(value) => handleFilterChange('clientId', value)}
                  displayKey="name"
                  valueKey="id"
                  placeholder="All clients"
                  searchPlaceholder="Search clients..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Product</label>
                <SearchableDropdown
                  options={productOptions}
                  value={filters.productId}
                  onChange={(value) => handleFilterChange('productId', value)}
                  displayKey="name"
                  valueKey="id"
                  placeholder="All products"
                  searchPlaceholder="Search products..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <SearchableDropdown
                  options={categoryOptions}
                  value={filters.categoryId}
                  onChange={(value) => handleFilterChange('categoryId', value)}
                  displayKey="name"
                  valueKey="id"
                  placeholder="All categories"
                  searchPlaceholder="Search categories..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Invoice Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All statuses</option>
                  {statusChoices.map(value => (
                    <option key={value} value={value}>{humanise(value)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">FBR Status</label>
                <select
                  value={filters.fbrStatus}
                  onChange={(e) => handleFilterChange('fbrStatus', e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All FBR statuses</option>
                  {fbrStatusChoices.map(value => (
                    <option key={value} value={value}>{humanise(value)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Totals for the filtered period */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-slate-100">
              {[
                { label: 'Sales in period', value: formatLargeNumber(periodSummary?.total_revenue) },
                { label: 'Invoices', value: periodSummary?.total_invoices ?? 0 },
                { label: 'Sales tax', value: formatLargeNumber(periodSummary?.total_tax) },
                { label: 'Average invoice', value: formatLargeNumber(periodSummary?.avg_invoice_value) },
                { label: 'Clients billed', value: periodSummary?.unique_clients ?? 0 }
              ].map(stat => (
                <div key={stat.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{stat.label}</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {analyticsLoading ? '...' : stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue Trend Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100">
              <h4 className="text-lg font-medium text-slate-900">Revenue Trend</h4>
            </div>
            <div className="p-6">
              {analyticsLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                </div>
              ) : trendRows.length > 0 ? (
                <AnalyticsChart
                  type="line"
                  data={trendRows.map(item => ({
                    label: item.period,
                    value: item.revenue,
                    revenue: item.revenue,
                    count: item.invoice_count
                  }))}
                  height={250}
                  colors={['#3B82F6']}
                  formatValue={formatLargeNumber}
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No sales in this period
                </div>
              )}
            </div>
          </div>

          {/* FBR Status Distribution */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100">
              <h4 className="text-lg font-medium text-slate-900">FBR Status Distribution</h4>
            </div>
            <div className="p-6">
              {analyticsLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                </div>
              ) : analyticsData.fbrCompliance?.fbr_status_breakdown?.length > 0 ? (
                <AnalyticsChart
                  type="pie"
                  data={analyticsData.fbrCompliance.fbr_status_breakdown.map(item => ({
                    label: item.status.replace('_', ' '),
                    value: item.count,
                    count: item.count,
                    revenue: item.revenue
                  }))}
                  height={250}
                  colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444']}
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No FBR compliance data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Clients */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100">
              <h4 className="text-lg font-medium text-slate-900">Top Clients by Revenue</h4>
            </div>
            <div className="p-6">
              {analyticsLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                </div>
              ) : analyticsData.topPerformers?.top_clients?.length > 0 ? (
                <AnalyticsChart
                  type="bar"
                  data={analyticsData.topPerformers.top_clients.map(item => ({
                    label: item.client_name,
                    value: item.revenue,
                    revenue: item.revenue,
                    count: item.invoice_count
                  }))}
                  height={250}
                  colors={['#8B5CF6']}
                  formatValue={formatLargeNumber}
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No client data available
                </div>
              )}
            </div>
          </div>

          {/* Product Performance */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100">
              <h4 className="text-lg font-medium text-slate-900">Top Products by Sales</h4>
            </div>
            <div className="p-6">
              {analyticsLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                </div>
              ) : analyticsData.topPerformers?.top_products?.length > 0 ? (
                <AnalyticsChart
                  type="bar"
                  data={analyticsData.topPerformers.top_products.map(item => ({
                    label: item.product_name,
                    value: item.revenue,
                    revenue: item.revenue,
                    count: item.units_sold
                  }))}
                  height={250}
                  colors={['#10B981']}
                  formatValue={formatLargeNumber}
                />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No product data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </Navigation>
  );
};

export default Dashboard;
